import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormArray, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PromptSetApiService, FieldType, DoraFieldConfig, OutputSchema } from '../../shared/api.service';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-output-schema',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatIconModule,
        MatChipsModule,
        MatCardModule,
        MatExpansionModule,
        TranslateModule
    ],
    templateUrl: './output-schema.component.html',
    styleUrl: './output-schema.component.scss'
})
export class OutputSchemaComponent implements OnInit, OnDestroy, OnChanges {
    @Input() promptSetId!: string;
    @Input() promptSetName!: string;
    @Input() currentOutputSchema?: OutputSchema;
    @Output() schemaSaved = new EventEmitter<OutputSchema>();

    schemaForm: FormGroup;
    fieldTypes = Object.values(FieldType);
    isSaving = false;
    private subscriptions: Subscription[] = [];

    // User-friendly labels for field types (for business users)
    fieldTypeLabels: Record<FieldType, string> = {
        [FieldType.STR]: 'Text',
        [FieldType.INT]: 'Whole Number',
        [FieldType.FLOAT]: 'Decimal Number',
        [FieldType.DICT]: 'Object',
        [FieldType.LIST]: 'List'
    };

    getFieldTypeLabel(type: FieldType): string {
        return this.fieldTypeLabels[type] || type;
    }

    // Suggested field names with descriptions
    suggestedFields = [
        { value: 'short_answer', label: 'short_answer', description: 'Brief answer (e.g., yes, no, to be checked)' },
        { value: 'summary', label: 'summary', description: 'Detailed explanation or reasoning' },
        { value: 'passage', label: 'passage', description: 'Relevant text excerpt from the document' },
        { value: 'compliance_status', label: 'compliance_status', description: 'Compliance assessment result' },
        { value: 'risk_level', label: 'risk_level', description: 'Risk assessment (low, medium, high)' },
        { value: 'recommendation', label: 'recommendation', description: 'Suggested actions or next steps' },
        { value: 'contract_value', label: 'contract_value', description: 'Monetary value or numerical data' },
        { value: 'confidence_score', label: 'confidence_score', description: 'Confidence level of the analysis' },
        { value: 'custom', label: 'Custom field name...', description: 'Enter your own field name' }
    ];

    constructor(
        private fb: FormBuilder,
        private apiService: PromptSetApiService,
        private snackBar: MatSnackBar
    ) {
        this.schemaForm = this.fb.group({
            fields: this.fb.array([])
        });
    }

    ngOnInit(): void {
        this.loadSchema();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Reload schema when currentOutputSchema input changes
        if (changes['currentOutputSchema'] && !changes['currentOutputSchema'].firstChange) {
            // Clear existing fields
            while (this.fields.length !== 0) {
                this.fields.removeAt(0);
            }
            // Reload schema
            this.loadSchema();
        }
    }

    private loadSchema(): void {
        // Load existing schema if available
        if (this.currentOutputSchema?.fields && this.currentOutputSchema.fields.length > 0) {
            this.currentOutputSchema.fields.forEach(field => {
                this.addField(field);
            });
        } else {
            // Add one empty field by default
            this.addField();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    get fields(): FormArray {
        return this.schemaForm.get('fields') as FormArray;
    }

    createFieldGroup(field?: DoraFieldConfig): FormGroup {
        const fieldGroup = this.fb.group({
            field_name_select: [field?.field_name || ''],
            field_name_custom: [''],
            field_name: [
                field?.field_name || '',
                [
                    Validators.required,
                    Validators.pattern(/^[a-z0-9_]+$/),
                    this.uniqueFieldNameValidator.bind(this)
                ]
            ],
            field_type: [field?.field_type || FieldType.STR, Validators.required],
            default_value: [field?.default_value || ''],
            expected_values: [field?.expected_values || []]
        });

        // If loading existing field, check if it's a suggested field or custom
        if (field?.field_name) {
            const isSuggested = this.suggestedFields.some(sf => sf.value === field.field_name);
            if (isSuggested) {
                fieldGroup.patchValue({ field_name_select: field.field_name });
            } else {
                fieldGroup.patchValue({ 
                    field_name_select: 'custom',
                    field_name_custom: field.field_name 
                });
            }
        }

        return fieldGroup;
    }

    onFieldNameSelectChange(fieldIndex: number): void {
        const fieldGroup = this.fields.at(fieldIndex) as FormGroup;
        const selectedValue = fieldGroup.get('field_name_select')?.value;

        if (selectedValue === 'custom') {
            // Clear the actual field_name and let user type custom name
            fieldGroup.patchValue({ field_name: '' });
        } else {
            // Use the selected suggested field name
            fieldGroup.patchValue({ 
                field_name: selectedValue,
                field_name_custom: '' 
            });
        }

        // Trigger validation
        fieldGroup.get('field_name')?.updateValueAndValidity();
    }

    onCustomFieldNameChange(fieldIndex: number): void {
        const fieldGroup = this.fields.at(fieldIndex) as FormGroup;
        const customValue = fieldGroup.get('field_name_custom')?.value;

        // Update the actual field_name with the custom value
        fieldGroup.patchValue({ field_name: customValue });

        // Trigger validation
        fieldGroup.get('field_name')?.updateValueAndValidity();
    }

    isCustomFieldName(fieldIndex: number): boolean {
        const fieldGroup = this.fields.at(fieldIndex) as FormGroup;
        return fieldGroup.get('field_name_select')?.value === 'custom';
    }

    uniqueFieldNameValidator(control: AbstractControl): ValidationErrors | null {
        if (!control.value) {
            return null;
        }

        const currentFieldName = control.value.toLowerCase();
        const fieldsArray = this.fields;
        
        if (!fieldsArray) {
            return null;
        }

        let count = 0;
        for (let i = 0; i < fieldsArray.length; i++) {
            const fieldGroup = fieldsArray.at(i) as FormGroup;
            const fieldNameControl = fieldGroup.get('field_name');
            if (fieldNameControl && fieldNameControl.value?.toLowerCase() === currentFieldName) {
                count++;
            }
        }

        return count > 1 ? { duplicateFieldName: true } : null;
    }

    addField(field?: DoraFieldConfig): void {
        this.fields.push(this.createFieldGroup(field));
    }

    removeField(index: number): void {
        if (this.fields.length > 1) {
            this.fields.removeAt(index);
        } else {
            this.snackBar.open('At least one field is required', 'Close', {
                duration: 3000,
                horizontalPosition: 'end',
                verticalPosition: 'top'
            });
        }
    }

    addExpectedValue(fieldIndex: number, event: any): void {
        const input = event.target as HTMLInputElement;
        const value = input.value.trim();
        
        if (value) {
            const fieldGroup = this.fields.at(fieldIndex) as FormGroup;
            const expectedValues = fieldGroup.get('expected_values')?.value || [];
            
            if (!expectedValues.includes(value)) {
                expectedValues.push(value);
                fieldGroup.patchValue({ expected_values: expectedValues });
            }
            
            input.value = '';
        }
    }

    removeExpectedValue(fieldIndex: number, valueIndex: number): void {
        const fieldGroup = this.fields.at(fieldIndex) as FormGroup;
        const expectedValues = [...(fieldGroup.get('expected_values')?.value || [])];
        expectedValues.splice(valueIndex, 1);
        fieldGroup.patchValue({ expected_values: expectedValues });
    }

    onExpectedValueKeydown(fieldIndex: number, event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            event.preventDefault();
            this.addExpectedValue(fieldIndex, event);
        }
    }

    saveSchema(): void {
        if (this.schemaForm.invalid) {
            this.snackBar.open('Please fix validation errors before saving', 'Close', {
                duration: 5000,
                horizontalPosition: 'end',
                verticalPosition: 'top',
                panelClass: ['error-snackbar']
            });
            return;
        }

        this.isSaving = true;
        const fields: DoraFieldConfig[] = this.fields.value.map((f: any) => ({
            field_name: f.field_name,
            field_type: f.field_type,
            default_value: f.default_value,
            expected_values: f.expected_values
        }));

        const sub = this.apiService.updateOutputSchema(this.promptSetId, this.promptSetName, fields).subscribe({
            next: (response) => {
                this.isSaving = false;
                this.snackBar.open('Output schema saved successfully', 'Close', {
                    duration: 3000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['success-snackbar']
                });
                // Emit the saved schema to parent component
                this.schemaSaved.emit({ fields });
            },
            error: (error) => {
                this.isSaving = false;
                console.error('Error saving output schema:', error);
                this.snackBar.open('Error saving output schema', 'Close', {
                    duration: 5000,
                    horizontalPosition: 'end',
                    verticalPosition: 'top',
                    panelClass: ['error-snackbar']
                });
            }
        });

        this.subscriptions.push(sub);
    }

    getFieldErrorMessage(fieldIndex: number, controlName: string): string {
        const fieldGroup = this.fields.at(fieldIndex) as FormGroup;
        const control = fieldGroup.get(controlName);

        if (!control || !control.errors) {
            return '';
        }

        if (control.errors['required']) {
            return 'This field is required';
        }

        if (control.errors['pattern']) {
            return 'Only lowercase letters, numbers, and underscores allowed';
        }

        if (control.errors['duplicateFieldName']) {
            return 'Field name must be unique';
        }

        return '';
    }
}
